import { HistoricalValueAreaChart } from '@/components/charts/historicalValueChart'
import { LoadingIcon } from '@/components/icons/loading'
import { useSettingsContext } from '@/context/settings'
import { useReturnsQuery } from '@/queries/returns.query'
import type { TransactionsContext } from '@/queries/transactionsContext.query'
import { useVaultHolderEventsQuery } from '@/queries/vaultHolderEvents.query'
import type { Vault } from '@/types/vault'
import { getApyValue } from '@/utils/apy'
import { useMemo } from 'react'
import { type Address, formatUnits } from 'viem'
import { PositionActivity } from './positionActivity'
import { PositionSummary } from './positionSummary'

type PositionDetailsProps = {
  address: Address | undefined
  vault: Vault
  transactionsContext: TransactionsContext
}

export function PositionDetails({ address, vault, transactionsContext }: PositionDetailsProps) {
  const { asset, apy } = vault
  const { events, isLoading, historicalChartData } = useVaultHolderEventsQuery({ address, vault })
  const { returnsNative, isLoading: isReturnsLoading } = useReturnsQuery({ address, vault })
  const { apyRange, includeRewards } = useSettingsContext()
  const totalPositionValue =
    BigInt(transactionsContext?.asset.positionValueInAsset ?? 0) +
    (transactionsContext?.childrenPositions?.reduce(
      (total, position) => total + BigInt(position.asset.positionValueInAsset ?? 0),
      0n
    ) ?? 0n)

  const chartData = useMemo(() => {
    if (transactionsContext === undefined) return historicalChartData
    const value = Number(formatUnits(totalPositionValue, asset.decimals))

    return [...historicalChartData, { timestamp: Date.now(), value }]
  }, [transactionsContext, historicalChartData, totalPositionValue, asset.decimals])

  return (
    <div className="flex flex-col gap-4 md:gap-5">
      <PositionSummary
        returnsNative={returnsNative}
        isReturnsLoading={isReturnsLoading}
        vault={vault}
        positionValue={totalPositionValue}
      />
      {isLoading ? (
        <div className="flex w-full flex-col items-center justify-center gap-4 py-12">
          <LoadingIcon size={48} className="animate-spin" />
          <p className="font-bold text-xl">Loading position details...</p>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-4 rounded-2xl border p-4 md:p-5">
            <h4 className="font-medium text-heading-foreground md:text-lg">Historical position value</h4>
            <HistoricalValueAreaChart
              data={chartData}
              annualApy={getApyValue({ apy: apy, apyRange, includeRewards })}
              asset={asset}
            />
          </div>
          <PositionActivity asset={asset} events={events} />
        </>
      )}
    </div>
  )
}
